import { Component, ErrorInfo, ReactNode } from "react";
import { useI18n } from "@expertloop/i18n/react";

interface Props {
	children: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
}

function ErrorBoundaryFallback({
	error,
	onReload,
	onGoHome,
}: {
	error: Error | null;
	onReload: () => void;
	onGoHome: () => void;
}) {
	const { t } = useI18n();

	return (
		<div className="error-boundary">
			<div className="error-boundary-content">
				<h1>{t("adminError.title")}</h1>
				<p>{t("adminError.body")}</p>
				{error && (
					<details>
						<summary>{t("adminError.details")}</summary>
						<pre>{error.message}</pre>
					</details>
				)}
				<div className="error-boundary-actions">
					<button onClick={onReload} className="btn btn-primary">
						{t("adminError.reload")}
					</button>
					<button onClick={onGoHome} className="btn btn-secondary">
						{t("adminError.goHome")}
					</button>
				</div>
			</div>
			<style>{`
				.error-boundary {
					display: flex;
					justify-content: center;
					align-items: center;
					min-height: 100vh;
					padding: 2rem;
					background: #f5f5f5;
				}
				.error-boundary-content {
					max-width: 500px;
					padding: 2rem;
					background: white;
					border-radius: 8px;
					box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
					text-align: center;
				}
				.error-boundary h1 {
					color: #dc3545;
					margin-bottom: 1rem;
				}
				.error-boundary p {
					color: #666;
					margin-bottom: 1.5rem;
				}
				.error-boundary details {
					text-align: left;
					margin-bottom: 1.5rem;
					padding: 1rem;
					background: #f8f9fa;
					border-radius: 4px;
				}
				.error-boundary summary {
					cursor: pointer;
					font-weight: 500;
					margin-bottom: 0.5rem;
				}
				.error-boundary pre {
					margin: 0;
					font-size: 0.85rem;
					white-space: pre-wrap;
					word-break: break-word;
					color: #dc3545;
				}
				.error-boundary-actions {
					display: flex;
					gap: 1rem;
					justify-content: center;
				}
				.error-boundary .btn {
					padding: 0.75rem 1.5rem;
					border: none;
					border-radius: 4px;
					font-size: 1rem;
					cursor: pointer;
					transition: background-color 0.2s;
				}
				.error-boundary .btn-primary {
					background: #0d6efd;
					color: white;
				}
				.error-boundary .btn-primary:hover {
					background: #0b5ed7;
				}
				.error-boundary .btn-secondary {
					background: #6c757d;
					color: white;
				}
				.error-boundary .btn-secondary:hover {
					background: #5c636a;
				}
			`}</style>
		</div>
	);
}

export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error("ErrorBoundary caught an error:", error, errorInfo);
	}

	handleReload = () => {
		window.location.reload();
	};

	handleGoHome = () => {
		window.location.href = import.meta.env.PROD ? "/admin/" : "/";
	};

	render() {
		if (this.state.hasError) {
			return (
				<ErrorBoundaryFallback
					error={this.state.error}
					onReload={this.handleReload}
					onGoHome={this.handleGoHome}
				/>
			);
		}

		return this.props.children;
	}
}
